import { applySnapshot, flow, Instance, types } from 'mobx-state-tree';
import { IConstant } from 'types';
import { Constants } from 'http-requests';
import type { NewsConstantsDTO } from 'http-requests/news';
import { RequestStateModel } from '../general-models';

type ValueOf<K extends keyof NewsConstantsDTO> =
  NewsConstantsDTO[K][number]['value'];

const field = <T extends string>() =>
  types.optional(types.array(types.frozen<IConstant<T>>()), []);

const EXPIRY_OPTIONS: IConstant<'always' | '7d' | '14d' | '1m'>[] = [
  { value: 'always', text: 'Всегда' },
  { value: '7d', text: '7 дней' },
  { value: '14d', text: '14 дней' },
  { value: '1m', text: '1 месяц' },
];

const PUBLISH_DATE_OPTIONS: IConstant<'now' | 'tomorrow' | 'custom'>[] = [
  { value: 'now', text: 'Сейчас' },
  { value: 'tomorrow', text: 'Завтра' },
  { value: 'custom', text: 'Другая дата' },
];

export const NewsConstantsModel = types
  .model('NewsConstantsModel', {
    PublicationType: field<ValueOf<'PublicationType'>>(),
    PublicationState: field<ValueOf<'PublicationState'>>(),
    ProviderDeliveryKeysChoices:
      field<ValueOf<'ProviderDeliveryKeysChoices'>>(),
    EventType: field<ValueOf<'EventType'>>(),
    EventResource: field<ValueOf<'EventResource'>>(),
    TelephonogramType: field<ValueOf<'TelephonogramType'>>(),
    AudienceType: field<ValueOf<'AudienceType'>>(),
    news_categories: types.optional(
      types.model({
        user: field<ValueOf<'NewsCategory'>>(),
        system: field<ValueOf<'NewsSystemCategory'>>(),
      }),
      {}
    ),
    NewsRecipientsFilter: field<ValueOf<'NewsRecipientsFilter'>>(),
    StatisticsLabels: field<ValueOf<'StatisticsLabels'>>(),
    VisibilityOptions: field<ValueOf<'VisibilityOptions'>>(),
  })
  .volatile(() => ({
    status: RequestStateModel.create({}),
  }))
  .views((self) => ({
    get expiry_options() {
      return EXPIRY_OPTIONS;
    },
    get publish_date_options() {
      return PUBLISH_DATE_OPTIONS;
    },
    get labels() {
      const toMap = <T extends string>(
        arr: IConstant<T>[]
      ): Record<T, string> =>
        Object.fromEntries(
          arr.map(({ value, text }) => [value, text])
        ) as Record<T, string>;

      return {
        publication_types: toMap(self.PublicationType),
        publication_states: toMap(self.PublicationState),
        provider_delivery_keys_choices: toMap(self.ProviderDeliveryKeysChoices),
        event_types: toMap(self.EventType),
        event_resources: toMap(self.EventResource),
        telephonogram_types: toMap(self.TelephonogramType),
        audiences: toMap(self.AudienceType),
        statistics_labels: toMap(self.StatisticsLabels),
        expiry_options: toMap(EXPIRY_OPTIONS),
        publish_date_options: toMap(PUBLISH_DATE_OPTIONS),
        user_news_categories: toMap(self.news_categories.user),
        system_news_categories: toMap(self.news_categories.system),
        all_news_categories: {
          ...toMap(self.news_categories.user),
          ...toMap(self.news_categories.system),
        },
      };
    },
  }))
  .actions((self) => ({
    fetch: flow(function* () {
      try {
        self.status.loadingStates();

        const response: { results: NewsConstantsDTO } =
          yield Constants.GetNewsConstants();

        const { NewsCategory, NewsSystemCategory, ...rest } = response.results;

        applySnapshot(self, {
          ...rest,
          news_categories: {
            user: NewsCategory.filter(
              ({ value }) => !NewsSystemCategory.some((s) => s.value === value)
            ),
            system: NewsSystemCategory,
          },
        });

        self.status.successStates();
      } catch (error) {
        self.status.errorStates();
        console.error(error);
      } finally {
        self.status.finallyStates();
      }
    }),
  }));

export interface INewsConstantsModel extends Instance<
  typeof NewsConstantsModel
> {}
